/**
 * O papel A4 da conferência CEGA.
 *
 * Cega quer dizer uma coisa só, e é o que dá valor ao papel: o conferente
 * **não vê a quantidade esperada**. Ele conta o que chegou e escreve. Se o
 * número previsto estivesse impresso ao lado, a contagem deixaria de ser
 * contagem e viraria confirmação — que é exatamente o erro que a conferência
 * cega existe para evitar.
 *
 * Por isso este componente recebe a lista SEM quantidade nenhuma. Não é
 * `display:none`: o número não chega até aqui, nem vem do servidor.
 *
 * O LAYOUT SAIU DO RELATÓRIO DO ERP
 * ---------------------------------
 * Colunas, ordem, rótulos e assinaturas foram conferidos contra o PDF do
 * `RSG-7402-03` do Procfit. O que mudou é o acabamento: hierarquia
 * tipográfica, separação forte entre itens e caixas de contagem com
 * linha-guia. Este papel vai para o box, numa prancheta, sob luz ruim, e volta
 * para ser digitado por outra pessoa — cada uma dessas escolhas existe por
 * causa disso.
 */

export interface ItemCego {
  produto: number;
  descricao: string | null;
  /** Os 8 primeiros dígitos do EAN, como o `RSG-7402-03` imprime. */
  ean8: string | null;
  /** Unidade da embalagem (UN, CX…). */
  tipo: string | null;
  /** Unidades por embalagem — não é a quantidade do recebimento. */
  qtdeEmbalagem: number | null;
  pedidoCompra: number | null;
}

export interface CabecalhoCego {
  recebimento: number;
  fornecedorCodigo: number | null;
  data: string | null;
  fornecedor: string | null;
  notaFiscal: number | null;
  serie: string | null;
  emissao: string | null;
  volumes: number;
  notas: number;
  empresa?: number | null;
  centroEstoque?: number | null;
}

/**
 * O CSS do papel.
 *
 * Em milímetros e com `@page`, porque o alvo é a folha e não a tela. Quatro
 * decisões que só aparecem no papel impresso:
 *
 *  - `print-color-adjust: exact` — sem isso o navegador descarta os fundos, e
 *    a faixa do cabeçalho e a zebra somem justamente quando servem;
 *  - `thead { display: table-header-group }` — repete o cabeçalho da tabela em
 *    toda folha; sem ele, da página 2 em diante ninguém sabe qual coluna é a
 *    da contagem;
 *  - `break-inside: avoid` na linha — item partido entre duas folhas é item
 *    contado duas vezes ou nenhuma;
 *  - a SEPARAÇÃO entre itens é uma régua escura, não uma linha fina: quem está
 *    com a prancheta na mão precisa enxergar onde um item termina e o outro
 *    começa sem seguir a linha com o dedo.
 */
export const CSS_CONFERENCIA_CEGA = `
@media print {
  @page { size: A4 portrait; margin: 8mm 7mm 14mm 7mm; }
  .cega-nao-imprimir { display: none !important; }
}

.cega {
  font-family: "Segoe UI", ui-sans-serif, system-ui, sans-serif;
  color: #14161a; background: #fff; font-size: 8.6pt; line-height: 1.3;
}
.cega * { print-color-adjust: exact; -webkit-print-color-adjust: exact; box-sizing: border-box; }

/* ---------------------------------------------------------------- topo --- */
.cega .topo {
  display: flex; justify-content: space-between; align-items: stretch;
  border: 0.3mm solid #14161a; border-bottom: none;
}
.cega .topo-id { padding: 2.4mm 3mm; flex: 1; }
.cega .topo-doc {
  padding: 2.4mm 3mm; text-align: right; min-width: 42mm;
  background: #14161a; color: #fff; display: flex; flex-direction: column; justify-content: center;
}
.cega .marca { font-size: 11.5pt; font-weight: 800; letter-spacing: -0.01em; line-height: 1.1; }
.cega .subtitulo { font-size: 9.2pt; font-weight: 600; margin-top: 0.6mm; }
.cega .codigo-rel { font-size: 6.6pt; color: #6b7280; margin-top: 1mm; letter-spacing: 0.06em; }
.cega .doc-rotulo { font-size: 6.6pt; text-transform: uppercase; letter-spacing: 0.12em; opacity: 0.75; }
.cega .doc-numero { font-size: 19pt; font-weight: 800; line-height: 1; font-variant-numeric: tabular-nums; }

/* ------------------------------------------------------------- dados ---- */
.cega .dados { display: flex; border: 0.3mm solid #14161a; }
.cega .campo { padding: 1.8mm 3mm; border-right: 0.2mm solid #c7cbd1; flex: 1; min-width: 0; }
.cega .campo:last-child { border-right: none; }
.cega .rot { font-size: 6.4pt; text-transform: uppercase; letter-spacing: 0.09em; color: #6b7280; display: block; }
.cega .val { font-size: 9pt; font-weight: 600; margin-top: 0.4mm; word-break: break-word; }

/* ------------------------------------------------------------ tabela ---- */
.cega table { width: 100%; border-collapse: collapse; margin-top: 2.5mm; }
.cega thead { display: table-header-group; }
.cega th {
  background: #14161a; color: #fff; font-size: 6.8pt; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.07em; text-align: left;
  padding: 1.6mm 1.6mm; border: 0.2mm solid #14161a;
}
.cega td {
  border-left: 0.2mm solid #c7cbd1; border-right: 0.2mm solid #c7cbd1;
  padding: 1.6mm 1.6mm; vertical-align: middle;
}

/*
  A DIVISÃO ENTRE ITENS.

  Régua escura de 0.45mm no pé de cada linha, mais zebra alternada. Borda fina
  uniforme faz 40 itens virarem um bloco cinza; aqui cada item é visivelmente
  uma faixa própria, que é o que o olho precisa para não anotar a contagem na
  linha do produto de cima.
*/
.cega tbody tr { break-inside: avoid; page-break-inside: avoid; }
.cega tbody td { border-bottom: 0.45mm solid #14161a; }
.cega tbody tr.zebra td { background: #eef1f5; }
.cega tbody tr:first-child td { border-top: 0.2mm solid #14161a; }

.cega .num { font-variant-numeric: tabular-nums; font-family: "Consolas", ui-monospace, monospace; }
.cega .dir { text-align: right; }
.cega .cen { text-align: center; }
.cega .prod { font-weight: 700; font-size: 9pt; }
.cega .desc { font-size: 8.4pt; }
/* O pedido de compra é contexto, não identificação: vai numa linha PRÓPRIA
   abaixo do nome. Inline, ele entrava no meio da quebra da descrição e
   sobrava um "· pedido 438212" órfão na segunda linha. */
.cega .pedido {
  display: block; margin-top: 0.5mm; font-size: 6.6pt; color: #6b7280;
  text-transform: uppercase; letter-spacing: 0.06em;
}

/*
  A CAIXA DE ESCREVER.

  Alta (10 mm) para caber número feito com a prancheta na mão, e SEMPRE branca
  — inclusive nas linhas zebradas. Campo para preencher com fundo cinza é campo
  que parece já preenchido; a seletividade extra (tr.zebra td.caixa) existe só
  para vencer a regra da zebra, que vem antes.

  A linha-guia para 1.6 mm acima da base para não encostar na régua escura do
  item — encostada, ela simplesmente desaparecia dentro dela.
*/
.cega .caixa { background: #fff; padding: 0; height: 10mm; }
.cega tbody tr.zebra td.caixa { background: #fff; }
.cega .caixa .guia { height: 100%; margin: 0 1.6mm 1.6mm; border-bottom: 0.3mm solid #8a919b; }

/* ------------------------------------------------------------ rodapé ---- */
.cega .resumo {
  display: flex; justify-content: space-between; align-items: center;
  border: 0.3mm solid #14161a; border-top: none;
  padding: 1.8mm 3mm; font-size: 7.4pt; background: #eef1f5;
}
.cega .assinaturas { display: flex; gap: 4mm; margin-top: 7mm; break-inside: avoid; }
.cega .assina { flex: 1; }
.cega .assina .linha { border-bottom: 0.3mm solid #14161a; height: 11mm; }
.cega .assina .nome {
  font-size: 6.8pt; text-transform: uppercase; letter-spacing: 0.08em;
  color: #374151; padding-top: 1mm; text-align: center;
}
.cega .emissao {
  margin-top: 3mm; padding-top: 1.4mm; border-top: 0.2mm solid #c7cbd1;
  display: flex; justify-content: space-between; font-size: 6.6pt; color: #6b7280;
}
`;

function dataBr(iso: string | null, comHora = false): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return comHora
    ? d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : d.toLocaleDateString('pt-BR');
}

export function ConferenciaCegaPapel({
  cabecalho,
  itens,
}: {
  cabecalho: CabecalhoCego;
  itens: ItemCego[];
}): JSX.Element {
  return (
    <div className="cega">
      <div className="topo">
        <div className="topo-id">
          <div className="marca">WMS Megamamute</div>
          <div className="subtitulo">Conferência Cega de Produtos do Recebimento</div>
          {/* O código do relatório do ERP fica visível: é por ele que alguém
              acha o original quando precisa comparar. */}
          <div className="codigo-rel">RSG-7402-03 · CONFERÊNCIA CEGA</div>
        </div>
        <div className="topo-doc">
          <span className="doc-rotulo">Recebimento</span>
          <span className="doc-numero">{cabecalho.recebimento}</span>
        </div>
      </div>

      <div className="dados">
        <div className="campo" style={{ flex: 2.4 }}>
          <span className="rot">Fornecedor</span>
          <span className="val">
            {cabecalho.fornecedorCodigo ? `${cabecalho.fornecedorCodigo} · ` : ''}
            {cabecalho.fornecedor ?? '—'}
          </span>
        </div>
        <div className="campo">
          <span className="rot">Nota fiscal</span>
          <span className="val num">
            {cabecalho.notaFiscal ?? '—'}
            {cabecalho.serie ? `/${cabecalho.serie}` : ''}
            {/* Mais de uma nota no mesmo recebimento é comum — o papel avisa,
                senão o conferente acha que falta mercadoria. */}
            {cabecalho.notas > 1 ? ` (+${cabecalho.notas - 1})` : ''}
          </span>
        </div>
        <div className="campo">
          <span className="rot">Emissão</span>
          <span className="val num">{dataBr(cabecalho.emissao)}</span>
        </div>
        <div className="campo">
          <span className="rot">Recebido em</span>
          <span className="val num">{dataBr(cabecalho.data)}</span>
        </div>
        <div className="campo" style={{ flex: 0.6 }}>
          <span className="rot">Volumes</span>
          <span className="val num">{cabecalho.volumes || '—'}</span>
        </div>
      </div>

      <table>
        <thead>
          {/* Colunas, ordem e rótulos conferidos contra o PDF do RSG-7402-03. */}
          <tr>
            <th style={{ width: '7mm' }} className="cen">
              #
            </th>
            <th style={{ width: '17mm' }}>Produto</th>
            <th>Descrição</th>
            <th style={{ width: '19mm' }}>EAN</th>
            <th style={{ width: '11mm' }} className="cen">
              Tipo
            </th>
            <th style={{ width: '14mm' }} className="dir">
              Qtd Emb.
            </th>
            <th style={{ width: '25mm' }} className="cen">
              Qtde contada
            </th>
            <th style={{ width: '27mm' }} className="cen">
              Lote / Nº série
            </th>
          </tr>
        </thead>
        <tbody>
          {itens.map((item, i) => (
            <tr key={`${item.produto}-${i}`} className={i % 2 === 1 ? 'zebra' : undefined}>
              <td className="cen num">{i + 1}</td>
              <td className="num prod">{item.produto}</td>
              <td className="desc">
                {item.descricao ?? '—'}
                {item.pedidoCompra ? (
                  <span className="pedido">Pedido de compra {item.pedidoCompra}</span>
                ) : null}
              </td>
              <td className="num">{item.ean8 ?? ''}</td>
              <td className="cen">{item.tipo ?? ''}</td>
              <td className="dir num">{item.qtdeEmbalagem ?? ''}</td>
              {/* VAZIAS, com linha-guia: é aqui que a conferência acontece. */}
              <td className="caixa">
                <div className="guia" />
              </td>
              <td className="caixa">
                <div className="guia" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="resumo">
        <span>
          <strong>
            {itens.length} {itens.length === 1 ? 'item' : 'itens'}
          </strong>{' '}
          para conferir
        </span>
        <span>
          A quantidade esperada <strong>não é impressa</strong> — conte e anote.
        </span>
      </div>

      {/*
        As assinaturas fecham o documento, e são duas de propósito: quem CONTA
        não é quem DIGITA. Quando aparece divergência, a primeira pergunta é
        exatamente essa, e o papel precisa responder sozinho.
      */}
      <div className="assinaturas">
        <div className="assina">
          <div className="linha" />
          <div className="nome">Assinatura do Conferente</div>
        </div>
        <div className="assina">
          <div className="linha" />
          <div className="nome">Assinatura do Digitador</div>
        </div>
        <div className="assina" style={{ flex: 0.5 }}>
          <div className="linha" />
          <div className="nome">Data / Hora</div>
        </div>
      </div>

      <div className="emissao">
        <span>Emitido em {dataBr(new Date().toISOString(), true)}</span>
        <span>
          Recebimento {cabecalho.recebimento}
          {cabecalho.empresa ? ` · empresa ${cabecalho.empresa}` : ''}
          {cabecalho.centroEstoque ? ` · CD ${cabecalho.centroEstoque}` : ''}
        </span>
      </div>
    </div>
  );
}
